import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CornerDownLeft, Plus, Search, Settings, type LucideIcon } from 'lucide-react';
import type { Me, MandantenSeite } from '@dxl/shared';
import { Kbd, Monogramm } from '@dxl/ui';
import { api } from '../lib/api';
import { initialen, navigationFuer, navZiel, plattformEinstellungenFuer } from '../lib/navigation';
import { darfPlattform } from '../lib/sitzung';
import { useVerzoegert } from '../lib/useVerzoegert';

interface Eintrag {
  id: string;
  gruppe: string;
  titel: string;
  zusatz?: string;
  symbol?: LucideIcon;
  monogramm?: string;
  farbe?: string;
  aus: () => void;
}

/** ⌘K: Springen zu Bereichen, Aktionen, Marktplätze suchen */
export function Befehlspalette({ me, offen, onSchliessen }: { me: Me; offen: boolean; onSchliessen: () => void }) {
  const navigate = useNavigate();
  const ref = useRef<HTMLDialogElement>(null);
  const vonAussen = useRef(false);
  const [text, setText] = useState('');
  const [aktiv, setAktiv] = useState(0);
  const q = useVerzoegert(text.trim(), 150);
  const plattform = darfPlattform(me, 'marktplaetze_lesen');
  const anlegen = darfPlattform(me, 'marktplaetze_bearbeiten');

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (offen && !d.open) {
      setText('');
      setAktiv(0);
      d.showModal();
    }
    if (!offen && d.open) {
      vonAussen.current = true;
      d.close();
    }
  }, [offen]);

  const treffer = useQuery({
    queryKey: ['palette-mandanten', q],
    queryFn: ({ signal }) => api<MandantenSeite>(`/api/plattform/mandanten?limit=6&q=${encodeURIComponent(q)}`, { signal }),
    enabled: offen && plattform && q.length > 0,
    staleTime: 10_000,
  });

  const eintraege = useMemo<Eintrag[]>(() => {
    const zu = (fn: () => void) => () => {
      onSchliessen();
      fn();
    };
    const t = q.toLowerCase();
    const liste: Eintrag[] = [];
    if (anlegen && !me.mandant && (!t || 'marktplatz anlegen neu'.includes(t))) {
      liste.push({ id: 'neu', gruppe: 'Aktionen', titel: 'Marktplatz anlegen', symbol: Plus, aus: zu(() => navigate({ to: '/marktplaetze', search: { neu: true } })) });
    }
    for (const g of navigationFuer(me)) {
      if (g.uebersicht && g.titel && (!t || `${g.titel} übersicht dashboard`.toLowerCase().includes(t))) {
        const ziel = navZiel(g.uebersicht);
        liste.push({ id: 'nav-' + g.uebersicht.id, gruppe: 'Navigation', titel: `${g.titel} – Übersicht`, zusatz: g.titel, symbol: g.punkte[0]!.symbol, aus: zu(() => navigate(ziel)) });
      }
      for (const p of g.punkte) {
        if (t && !p.titel.toLowerCase().includes(t)) continue;
        liste.push({ id: 'nav-' + p.id, gruppe: 'Navigation', titel: p.titel, zusatz: p.gesperrt ? 'Nicht im Paket enthalten' : g.titel, symbol: p.symbol, aus: zu(() => navigate(navZiel(p))) });
      }
    }
    // Reiter der Plattform-Einstellungen (z. B. „Nutzer & Rechte“, „Backup“) – erst beim Suchen, damit die Liste ruhig bleibt
    if (plattform && !me.mandant && t) {
      for (const b of plattformEinstellungenFuer(me)) {
        if (!`${b.titel} einstellungen`.toLowerCase().includes(t)) continue;
        liste.push({ id: 'einst-' + b.id, gruppe: 'Einstellungen', titel: b.titel, zusatz: 'Einstellungen', symbol: Settings, aus: zu(() => navigate({ to: '/einstellungen', search: { bereich: b.id } })) });
      }
    }
    for (const m of treffer.data?.eintraege ?? []) {
      liste.push({ id: 'm-' + m.id, gruppe: 'Marktplätze', titel: m.name, zusatz: m.kuerzel, monogramm: initialen(m.name), farbe: m.kuerzel, aus: zu(() => navigate({ to: '/marktplaetze', search: { m: m.id } })) });
    }
    return liste;
  }, [q, me, plattform, anlegen, treffer.data, navigate, onSchliessen]);

  useEffect(() => setAktiv(0), [q]);

  function taste(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAktiv((a) => Math.min(a + 1, eintraege.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAktiv((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      eintraege[aktiv]?.aus();
    }
  }

  let letzteGruppe = '';
  return (
    <dialog
      ref={ref}
      className="palette"
      onClose={() => {
        if (vonAussen.current) vonAussen.current = false;
        else onSchliessen();
      }} onClick={(e) => e.target === ref.current && onSchliessen()} aria-label="Befehle und Suche">
      <div className="palette__eingabe">
        <Search aria-hidden />
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={taste}
          placeholder={plattform ? 'Bereich, Aktion oder Marktplatz suchen' : 'Bereich oder Aktion suchen'}
          role="combobox"
          aria-expanded
          aria-controls="palette-liste"
          aria-activedescendant={eintraege[aktiv] ? `palette-${eintraege[aktiv]!.id}` : undefined}
        />
        <Kbd>Esc</Kbd>
      </div>
      <ul id="palette-liste" className="palette__liste" role="listbox">
        {eintraege.map((e, i) => {
          const kopf = e.gruppe !== letzteGruppe ? e.gruppe : null;
          letzteGruppe = e.gruppe;
          const S = e.symbol;
          return (
            <li key={e.id} role="presentation">
              {kopf && <p className="palette__gruppe">{kopf}</p>}
              <button
                type="button"
                id={`palette-${e.id}`}
                role="option"
                aria-selected={i === aktiv}
                className={i === aktiv ? 'aktiv' : undefined}
                onMouseMove={() => setAktiv(i)}
                onClick={e.aus}
              >
                {e.monogramm ? <Monogramm text={e.monogramm} farbeVon={e.farbe} groesse={20} /> : S ? <S aria-hidden /> : null}
                <span className="palette__titel">{e.titel}</span>
                {e.zusatz && <span className={e.monogramm ? 'palette__zusatz mono' : 'palette__zusatz'}>{e.zusatz}</span>}
                {i === aktiv && <CornerDownLeft className="palette__enter" aria-hidden />}
              </button>
            </li>
          );
        })}
        {eintraege.length === 0 && <li className="palette__leer">Keine Treffer für „{text}“</li>}
      </ul>
    </dialog>
  );
}
